<template>
  <div :class="['left-container', !printDesign && 'backgroundImg']">
    <div class="flex-container">
      <div class="event-title">
        {{ eventInfo.eventTitle }}
      </div>
      <div class="qr-code-container">
        <canvas v-if="!hidePrice" ref="qrcode" class="qrcode" :style="{ margin: 0, padding: 0 }" />
        <div class="qr-code-ticket-id-container">
          Ticked ID Nº <br>
          {{ getTicketId }}
        </div>
      </div>
    </div>
    <div class="event-date-and-price-container">
      <div class="event-date-container">
        <div class="event-date">{{ eventInfo.eventDate }}</div>
        <p class="event-address">{{ eventInfo.eventAddress }}</p>
      </div>

      <div class="price-and-fee-container">
        <div v-if="shouldShowTicketPrice" class="price-container">
          Cijena:
          <strong>{{ totalPriceRounded.toFixed(2) }} EUR</strong>, Osnovica:
          {{
            totalPriceRounded !== null
              ? (totalPriceRounded / (1 + getTicketVAT / 100)).toFixed(2)
              : ''
          }}
          EUR, PDV ({{ getTicketVAT }}%):
          {{ (totalPriceRounded - totalPriceRounded / (1 + getTicketVAT / 100)).toFixed(2) }}
          EUR*
        </div>
        <p v-if="!isTisakTicketSeller" class="fee-container">
          Naknada za izdavanje ulaznice: {{ round(getCoreEventFeeWithoutVat() * 1.25, 2) }} EUR, Osnovica:
          {{ getCoreEventFeeWithoutVat().toFixed(2) }} EUR, PDV (25%):
          {{ round(getCoreEventFeeWithoutVat() * 1.25 - getCoreEventFeeWithoutVat(), 2) }} EUR
        </p>
      </div>
    </div>
    <div class="ticket-info-container">
      <div v-if="ticketLocation && ticketLocationDto" class="ticket-location-container">
        <div
          v-for="(location, index) in ticketLocationDto"
          :key="index"
          class="ticket-location-field-container"
        >
          <div class="ticket-location-field-left">{{ index }}</div>
          <div class="ticket-location-field-right">
            <strong>{{ location }}</strong>
          </div>
        </div>
      </div>
      <div class="ticket-name-container">
        <div class="ticket-name">
          <div
            :style="{ fontSize: '10px', color: 'grey', margin: 0, lineHeight: '12px', padding: 0 }"
          >
            Naziv ulaznice
          </div>
          <strong
            :style="{ fontSize: '12px', color: 'black', margin: 0, lineHeight: '14px', padding: 0 }"
          >{{ ticketName }}</strong>
        </div>
        <div class="ticket-seller-container">
          <div v-if="!hidePrice">
            <div>{{ $t('purchasedIn') }}: {{ sellerName }}</div>
            <div>
              {{ $t('invoiceNumber') }}: {{ fiscalisationInfo.invoiceId }}, {{ $t('released') }}:
              {{ purchaseDate }}
            </div>
            <div v-if="!isCustomFiscalisation">
              {{ $t('issuedInTheName') }}: CoreEvent,<br>
              Dunjevac 15, 10000 Zagreb, Hrvatska<br>{{ $t('vat') }}: 36611335369
            </div>
            <div v-if="isCustomFiscalisation">
              {{ ticketSellerOrganization.name }},<br>
              {{ ticketSellerOrganization.address }}, {{ organization.postalCode }}
              {{ organization.city }}<br>{{ $t('vat') }}:
              {{ ticketSellerOrganization.oib }}
            </div>
            <div>{{ $t('order') }} #100{{ orderNumber }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { exchangeRate, parseTicketLocationDisplayFields, tisakTicketSeller } from '../../../src/utils'
import moment from 'moment'
import i18next from 'i18next'
import { round } from 'lodash'
import { PaymentType } from '@/enums/payment-type.js'
import { OrganizationCustomTicketFee } from '@/types'

const CORE_EVENT_FEE_EXEMPT_EVENT_IDS = [8595, 8693]

export default {
  name: 'pdf-ticket-left',
  props: [
    'hasBottomBorder',
    'eventInfo',
    'ticketLocation',
    'buyer',
    'ticketName',
    'ticketPrice',
    'orderPaymentType',
    'sellerName',
    'isCustomFiscalisation',
    'ticketSellerOrganization',
    'orderNumber',
    'purchasedAt',
    'fiscalisationInfo',
    'organization',
    'printDesign',
    'hidePrice',
    'qrcode',
    'ticketsInfo',
    'ticketIndex',
  ],
  data() {
    return {
      exchangeRate: exchangeRate,
      purchaseDate: `${moment(this.purchasedAt).format('D.M.YYYY., HH:mm')}h`,
      totalPriceRounded: 0,
      ticketLocationDto: null,
      coreEventFeePerTicketWithoutVat: null,
    }
  },
  computed: {
    shouldShowTicketPrice() {
      return !this.hidePrice || this.isFreeOrder
    },
    isTableTicket() {
      return this.ticketsInfo[this.ticketIndex].orderItem.itemMeta.table
    },
    isParterTicket() {
      return this.ticketsInfo[this.ticketIndex].orderItem.itemMeta.parter
    },
    getTicketId() {
      return this.ticketsInfo[this.ticketIndex].accreditation.id || ''
    },
    getTicketVAT() {
      return Number(this.ticketsInfo[this.ticketIndex].orderItem.id) === 9963
        ? 25
        : this.organization.VATPercentage
    },
    isFreeOrder() {
      return (
        this.orderPaymentType === PaymentType.FREE ||
        (this.buyer && this.buyer.paymentType === PaymentType.FREE)
      )
    },
    isTisakTicketSeller() {
      return (
        Number(this.ticketSellerOrganization && this.ticketSellerOrganization.id) === 1 ||
        String((this.ticketSellerOrganization && this.ticketSellerOrganization.name) || '').toLowerCase() ===
          tisakTicketSeller
      )
    },
  },
  async mounted() {
    this.generateQrCode(this.qrcode)
    this.roundTotalPrice(this.ticketPrice)
    if (this.ticketLocation && !this.isParterTicket) {
      this.setTicketLocation(this.ticketLocation)
    }
  },
  methods: {
    round,
    setTicketLocation(ticketLocation) {
      this.ticketLocationDto = parseTicketLocationDisplayFields(ticketLocation, {
        eventId: this.eventInfo.id,
        language: i18next.language,
        isTable: this.isTableTicket,
      })
    },
    getCoreEventFeeWithoutVat() {
      return this.ticketSellerOrganization && this.ticketSellerOrganization.fees && this.ticketSellerOrganization.fees.useEventFeePerTicket
        ? this.getCoreEventFeeForSelectedTicket(this.ticketPrice)
        : 0
    },
    getFixedFeeForSelectedTicket(ticketPrice) {
      let ticketFee = this.eventInfo.fixedFeePerTicket || this.organization.fixedFeePerTicket || 0
      const customOrganizationFees = OrganizationCustomTicketFee[Number(this.organization.id)]
      if (customOrganizationFees) {
        ticketFee = this.getTicketFee(ticketPrice, customOrganizationFees)
      }

      return ticketFee
    },
    getVariableFeePercentage() {
      return Number(
        this.eventInfo.variableFeePercentage ||
          (this.organization && this.organization.variableFeePercentage) ||
          0,
      )
    },
    getCoreEventFeeForSelectedTicket(ticketPrice) {
      if (CORE_EVENT_FEE_EXEMPT_EVENT_IDS.includes(Number(this.eventInfo.id))) {
        return 0
      }

      const price = Number(ticketPrice)
      if (!price || price <= 0) {
        return 0
      }

      const fixedFee = this.getFixedFeeForSelectedTicket(price)
      const variableFee = (price * this.getVariableFeePercentage()) / 100

      return round(fixedFee + variableFee, 2)
    },
    getTicketFee(price, organizationFees) {
      let ticketFee = 0
      for (const key in organizationFees) {
        const ranges = organizationFees[key]
        if (price >= ranges.min && price <= ranges.max) {
          return (ticketFee = Number(key))
        }
      }

      return ticketFee
    },
    roundTotalPrice(price) {
      const paymentType = (this.buyer && this.buyer.paymentType) || this.orderPaymentType || null
      let ticketPrice = Number(price)

      if (!paymentType) {
        return (this.totalPriceRounded = round(ticketPrice, 2))
      }

      if (paymentType === PaymentType.KEKS10) {
        ticketPrice = ticketPrice * 0.9
      } else if (paymentType === PaymentType.TELEGRAM20 || paymentType === PaymentType.POPUST20) {
        ticketPrice = ticketPrice * 0.8
      } else if (paymentType === PaymentType.Popust40) {
        ticketPrice = ticketPrice * 0.6
      } else if (paymentType === PaymentType.Popust50) {
        ticketPrice = ticketPrice * 0.5
      } else if (paymentType === PaymentType.FREE) {
        ticketPrice = 0
      } else if (
        [PaymentType.CASH20, PaymentType.CASH50, PaymentType.CARD20, PaymentType.CARD50].includes(
          paymentType,
        )
      ) {
        if (paymentType.endsWith('20')) {
          ticketPrice = ticketPrice * 0.8
        } else {
          ticketPrice = ticketPrice * 0.5
        }
      }

      return (this.totalPriceRounded = round(ticketPrice, 2))
    },
    async generateQrCode(value) {
      const canvas = this.$refs.qrcode

      return QRCode.toCanvas(canvas, value, {
        width: 65,
        margin: 0,
      })
    },
  },
}
</script>

<style scoped>
.left-container {
  height: 100%;
  width: calc(210mm * 0.8);
  padding: 5mm 0 0 6mm;
}

.backgroundImg {
  background-image: url('../../../public/assets/ticket-background.svg');
  background-size: cover;
}

.event-title {
  flex: 1 0 0;
  font-size: 22px;
  line-height: 24px;
  font-weight: 600;
}

.flex-container {
  display: flex;
}

.qr-code-container {
  text-align: center;
  width: 100px;
}

.qrcode {
  margin-top: 1mm;
  margin-right: 20px;
  height: 13mm !important;
  width: 13mm !important;
}

.qr-code-ticket-id-container {
  margin-top: 2px;
  font-size: 10px;
  color: #333333;
}

.event-date-and-price-container {
  display: flex;
  width: 100%;
  margin-top: 10px;
}

.event-date-container {
  flex: 1 0 0;
  border-left: 1px solid black;
  padding-left: 10px;
}

.ticket-location-container {
  font-size: 10px;
  color: #333333;
  display: flex;
  gap: 5px;
  flex: 1 0 0;
}

.ticket-location-field-container {
  border-left: 1px solid black;
  padding-left: 10px;
  flex: 1 0 0;
}

.ticket-location-field-left {
  font-size: 10px;
  color: #333333;
  margin: 0;
  padding: 0;
  line-height: 12px;
}

.ticket-location-field-right {
  font-size: 12px;
  color: black;
  margin: 0;
  padding: 0;
  line-height: 14px;
}

.event-date {
  font-size: 12px;
  font-weight: 600;
}

.event-address {
  font-size: 10px;
  color: #333333;
}

.price-and-fee-container {
  flex: 1 0 0;
  border-left: 1px solid black;
  padding-left: 10px;
}

.price-container {
  font-size: 12px;
}

.fee-container {
  font-size: 10px;
  color: grey;
  margin: 5px 0 0 0;
}

.ticket-info-container {
  display: flex;
  width: 100%;
  margin-top: 10px;
}

.ticket-name-container {
  display: flex;
  flex: 1 0 0;
  margin-right: 5px;
}

.ticket-name {
  border-left: 1px solid black;
  padding-left: 10px;
  flex: 1 0 0;
}

.ticket-seller-container {
  font-size: 6px;
  text-align: right;
  line-height: 8px;
}
</style>
